import { useStore } from '@nanostores/react';
import { memo, useEffect, useState } from 'react';
import { ApplicationBlock } from './ApplicationBlock';
import { SectionContainer } from './SectionContainer';
import { Markdown } from '~/components/chat/Markdown';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogActions, DialogButton, DialogContent, DialogHeader } from '~/components/ui/Dialog';
import { CLIENT_ORIGIN } from '~/lib/constants';
import { applicationManager, type ApplicationProvider } from '~/lib/stores/applicationManager';
import { APPLICATIONS } from '~/routes/connect.$provider';
import { withSpinner } from '~/utils/withSpinner';

export const Applications = memo(({}) => {
  const { applications, loading } = useApplications();

  const [disconnectedProvider, setDisconnectedProvider] = useState<ApplicationProvider | undefined>(undefined);
  const disconnectedProviderConfig = APPLICATIONS.get(disconnectedProvider!);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const connect = (provider: ApplicationProvider) => {
    applicationManager.connect(provider, { timeout: provider === 'SUPABASE' ? 120_000 : 30_000 });
  };

  const disconnect = (provider: ApplicationProvider) => {
    setDisconnectedProvider(provider);
  };

  const onConfirmDisconnect = async (provider: ApplicationProvider) => {
    setIsDisconnecting(true);

    await applicationManager.disconnect(provider);

    setDisconnectedProvider(undefined);
    setIsDisconnecting(false);
  };

  return (
    <SectionContainer title="Applications">
      {[...APPLICATIONS]
        .filter(([, /* provider */ { hidden }]) => !hidden)
        .map(([provider, { name, description, type, logo }]) => {
          const isConnected = applications[provider] === 'CONNECTED';

          return (
            <ApplicationBlock
              key={provider}
              type={type}
              title={name}
              logo={logo}
              description={description}
              action={
                <Button
                  size="md"
                  variant="secondary"
                  color={isConnected ? 'danger' : 'brand'}
                  loading={loading || applications[provider] === 'PENDING'}
                  disabled={applications[provider] === 'PENDING'}
                  onClick={() => {
                    if (isConnected) {
                      disconnect(provider);
                    } else {
                      connect(provider);
                    }
                  }}
                >
                  {applications[provider] === 'CONNECTED' ? 'Disconnect' : 'Connect'}
                </Button>
              }
            />
          );
        })}

      <ApplicationBlock
        title="GitHub"
        type="Code"
        logo={<div className="i-logos:github-icon?mask size-6" />}
        description={
          <>
            To revoke the GitHub Authorization visit{' '}
            <a
              href="https://github.com/settings/apps/authorizations"
              className="!text-bolt-elements-textSecondary underline"
              target="_blank"
            >
              github.com/settings/apps/authorizations
            </a>
            , look for the "Bolt (by StackBlitz)" application, and click "Revoke".
          </>
        }
      />

      <Button size="md" asChild variant="link-color" className="!no-underline items-start justify-end mt-5 gap-2 mb-4">
        <a href={`${CLIENT_ORIGIN}/settings/credentials`} target="_blank" className="text-sm decoration-none">
          <span className="i-heroicons:arrow-top-right-on-square text-lg" />
          Visit StackBlitz to manage how you log in
        </a>
      </Button>

      <Dialog
        open={Boolean(disconnectedProviderConfig)}
        maxWidth={450}
        showCloseButton={false}
        closeOnBackdropClick={false}
      >
        <DialogHeader title={`Disconnect ${disconnectedProviderConfig?.name}`} />

        <DialogContent>
          <p>You are about to disconnect {disconnectedProviderConfig?.name} from Bolt.</p>
          {disconnectedProviderConfig?.warning?.disconnect ? (
            <Markdown className="my-6 bg-alpha-orange-10 border-l border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500 [&_a]:underline [&_a]:text-bolt-elements-link hover:[&_a]:text-bolt-elements-linkHover">
              {disconnectedProviderConfig.warning.disconnect}
            </Markdown>
          ) : (
            <br />
          )}
          <p>Are you sure you want to disconnect?</p>
        </DialogContent>

        <DialogActions>
          <DialogButton
            variant="secondary"
            disabled={isDisconnecting}
            onClick={() => setDisconnectedProvider(undefined)}
          >
            Cancel
          </DialogButton>
          <DialogButton
            variant="tint"
            color="warning"
            loading={isDisconnecting}
            onClick={() => onConfirmDisconnect(disconnectedProvider!)}
          >
            Yes, Disconnect
          </DialogButton>
        </DialogActions>
      </Dialog>
    </SectionContainer>
  );
});

function useApplications() {
  const applications = useStore(applicationManager.applications);
  const [loading, setIsLoading] = useState(!applicationManager.hasConfiguration());

  useEffect(() => {
    withSpinner({
      action: async () => {
        await applicationManager.loadApplicationsConfiguration();
      },
      onStateUpdate: (isLoading) => {
        if (loading) {
          setIsLoading(isLoading);
        }
      },
      spinnerDelay: 0,
      minDuration: 350,
    });
  }, []);

  return {
    applications,
    loading,
  };
}
